import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, ListItemIcon, ListItemText, Menu, MenuItem, MenuList } from '@vapor/material'
import { useCallback, useState, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { CustomerDetailFragment, CustomerType } from 'src/api/api.generated'
import { HiCIcon } from 'src/app/components/icons/icon'
import CustomerDetail from 'src/app/pages/common/customer/customer-detail'
import { GuestOccupancy, MenuActionType } from '../../../../../types/guests.types'
import { faHouseChimneyUser, faCheck, faHourglassHalf, faWrench, faUser, faCalendarDays, faPersonCircleMinus, IconDefinition } from '@fortawesome/pro-regular-svg-icons'
import { ReservationContext } from 'src/app/pages/booking/reservation-detail/context/reservation-context'

export type EditButtonProps = {
  disabled: boolean
  value: GuestOccupancy
  onCustomerUpserted: (customer: CustomerDetailFragment) => void
  forceShowDates: boolean
  showDates: boolean
  onToggleShowDates: () => void
  showTaxExoneration: boolean
  onToggleShowTaxExoneration: () => void
  forceShowTaxExoneration: boolean
  inHouse?: MenuActionType
  notYetArrived?: MenuActionType
  departed?: MenuActionType
  reservedResourceId: string
}

export default function EditButton({
  disabled,
  value,
  onCustomerUpserted,
  forceShowDates,
  showDates,
  onToggleShowDates,
  showTaxExoneration,
  onToggleShowTaxExoneration,
  forceShowTaxExoneration,
  inHouse,
  notYetArrived,
  departed,
  reservedResourceId,
}: EditButtonProps) {
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const { optioned } = useContext(ReservationContext).reservation.reservationState

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const [customerDetailProps, setCustomerDetailProps] = useState<{ customerId: string } | { type: CustomerType } | null>(null)

  const onCustomerDetailClick = useCallback(() => {
    setCustomerDetailProps(value.guestId ? { customerId: value.guestId } : { type: CustomerType.PERSON })
    handleClose()
  }, [value.guestId])

  const onLocalCustomerUpserted = useCallback(
    (customer: CustomerDetailFragment) => {
      onCustomerUpserted(customer)
      setCustomerDetailProps(null)
    },
    [value, onCustomerUpserted],
  )

  const onToggleShowDatesClick = useCallback(() => {
    onToggleShowDates()
    handleClose()
  }, [onToggleShowDates])

  const onToggleShowTaxExonerationClick = useCallback(() => {
    onToggleShowTaxExoneration()
    handleClose()
  }, [onToggleShowTaxExoneration])

  const onMenuItemClick = (action: () => void) => {
    action()
    handleClose()
  }

  return (
    <>
      <Button //
        size="small"
        onClick={handleClick}
        color="primary"
        disabled={disabled}
        startIcon={<FontAwesomeIcon icon={faWrench} />}
        variant="text"
        sx={{
          textDecoration: 'underline !important',
        }}
      >
        {t('pms.booking.reservation.guest.action.edit_guest_button')}
      </Button>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuList>
          <StyledMenuItem onClick={onCustomerDetailClick} icon={faUser} text={t('pms.booking.reservation.guest.action.show_complete_customer')} />
          <StyledMenuItem
            onClick={onToggleShowDatesClick}
            disabled={forceShowDates}
            icon={faCalendarDays}
            text={showDates || forceShowDates ? t('pms.booking.reservation.guest.action.hide_occupancy_dates') : t('pms.booking.reservation.guest.action.show_occupancy_dates')}
          />
          <StyledMenuItem
            onClick={onToggleShowTaxExonerationClick}
            disabled={forceShowTaxExoneration}
            icon={faPersonCircleMinus}
            text={
              showTaxExoneration || forceShowTaxExoneration ? t('pms.booking.reservation.guest.action.hide_citytax_exoneration') : t('pms.booking.reservation.guest.action.show_citytax_exoneration')
            }
          />
          {inHouse && !optioned && (
            <StyledMenuItem
              onClick={() => onMenuItemClick(inHouse.action)}
              icon={faHouseChimneyUser}
              text={t('pms.booking.reservation.guest.action.set_phase', { phase: t('pms.booking.reservation.frontend_phase.home') })}
              disabled={inHouse.disabled}
            />
          )}
          {notYetArrived && (
            <StyledMenuItem
              onClick={() => onMenuItemClick(notYetArrived.action)}
              icon={faHourglassHalf}
              text={t('pms.booking.reservation.guest.action.set_phase', { phase: t('pms.booking.reservation.frontend_phase.not_yet_arrived') })}
              disabled={notYetArrived.disabled}
            />
          )}
          {departed && (
            <StyledMenuItem
              onClick={() => onMenuItemClick(departed.action)}
              icon={faCheck}
              text={t('pms.booking.reservation.guest.action.set_phase', { phase: t('pms.booking.reservation.frontend_phase.departed') })}
              disabled={departed.disabled}
            />
          )}
        </MenuList>
      </Menu>
      {customerDetailProps !== null && (
        <CustomerDetail //
          {...customerDetailProps}
          appearance={{
            open: customerDetailProps !== null,
            type: 'DIALOG',
            onClose: () => setCustomerDetailProps(null),
            maxWidth: '800px',
          }}
          onCancel={() => setCustomerDetailProps(null)}
          onUpserted={onLocalCustomerUpserted}
          guestOfReservedResourceId={reservedResourceId}
        />
      )}
    </>
  )
}

const StyledMenuItem = (props: { onClick: React.MouseEventHandler<HTMLLIElement>; icon: IconDefinition; text: string; disabled?: boolean }) => {
  return (
    <MenuItem onClick={props.onClick} disabled={props.disabled}>
      <ListItemIcon>
        <HiCIcon>
          <FontAwesomeIcon icon={props.icon} size="sm" style={{ width: 20 }} />
        </HiCIcon>
      </ListItemIcon>
      <ListItemText>{props.text}</ListItemText>
    </MenuItem>
  )
}
